/**
 * AuthContext — Supabase Auth State Management
 * 
 * Verwaltet: Login, Logout, Session, User-Profil, Rollen, Berechtigungen
 * Profil wird aus der mitarbeiter-Tabelle geladen (nicht mehr lokal).
 * Online-Status wird via Supabase Realtime Presence synchronisiert.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';
import type { User, Session, RealtimeChannel } from '@supabase/supabase-js';

// Mitarbeiter-Rollen
export type UserRole = 'admin' | 'sachbearbeiter' | 'mitarbeiter' | 'kiosk';

// Berechtigungs-Stufen
export type PermissionLevel = 0 | 1 | 2 | 3 | 4;
// 0 = kein Zugriff, 1 = lesen, 2 = erstellen, 3 = bearbeiten, 4 = löschen/vollzugriff

export interface UserPermissions {
  // Betriebe
  betrieb_gutachten: boolean;
  betrieb_autoglas: boolean;
  betrieb_mietwagen: boolean;
  betrieb_werkstatt: boolean;
  betrieb_protex: boolean;
  betrieb_unfallgiganten: boolean;
  betrieb_airbnb: boolean;
  betrieb_recht: boolean;
  // Module
  akten_level: PermissionLevel;
  kalender_level: PermissionLevel;
  aufgaben_level: PermissionLevel;
  buchhaltung_level: PermissionLevel;
  dokumente_level: PermissionLevel;
  kontakte_level: PermissionLevel;
  team_level: PermissionLevel;
  einstellungen_level: PermissionLevel;
  kommunikation_level: PermissionLevel;
  fahrzeuge_level: PermissionLevel;
}

// Rollen-Vorlagen für Berechtigungen
const ROLLEN_VORLAGEN: Record<string, UserPermissions> = {
  admin: {
    betrieb_gutachten: true, betrieb_autoglas: true, betrieb_mietwagen: true,
    betrieb_werkstatt: true, betrieb_protex: true, betrieb_unfallgiganten: true,
    betrieb_airbnb: true, betrieb_recht: true,
    akten_level: 4, kalender_level: 3, aufgaben_level: 4,
    buchhaltung_level: 4, dokumente_level: 4, kontakte_level: 3,
    team_level: 4, einstellungen_level: 4, kommunikation_level: 3, fahrzeuge_level: 3,
  },
  sachbearbeiter: {
    betrieb_gutachten: true, betrieb_autoglas: true, betrieb_mietwagen: true,
    betrieb_werkstatt: true, betrieb_protex: false, betrieb_unfallgiganten: false,
    betrieb_airbnb: false, betrieb_recht: false,
    akten_level: 4, kalender_level: 3, aufgaben_level: 4,
    buchhaltung_level: 0, dokumente_level: 3, kontakte_level: 3,
    team_level: 0, einstellungen_level: 0, kommunikation_level: 3, fahrzeuge_level: 3,
  },
  mitarbeiter: {
    betrieb_gutachten: true, betrieb_autoglas: false, betrieb_mietwagen: false,
    betrieb_werkstatt: false, betrieb_protex: false, betrieb_unfallgiganten: false,
    betrieb_airbnb: false, betrieb_recht: false,
    akten_level: 2, kalender_level: 1, aufgaben_level: 2,
    buchhaltung_level: 0, dokumente_level: 1, kontakte_level: 1,
    team_level: 0, einstellungen_level: 0, kommunikation_level: 1, fahrzeuge_level: 1,
  },
  kiosk: {
    betrieb_gutachten: false, betrieb_autoglas: false, betrieb_mietwagen: false,
    betrieb_werkstatt: false, betrieb_protex: false, betrieb_unfallgiganten: false,
    betrieb_airbnb: false, betrieb_recht: false,
    akten_level: 1, kalender_level: 1, aufgaben_level: 0,
    buchhaltung_level: 0, dokumente_level: 0, kontakte_level: 0,
    team_level: 0, einstellungen_level: 0, kommunikation_level: 0, fahrzeuge_level: 0,
  },
};

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  vorname: string;
  nachname: string;
  initialen: string;
  rolle: UserRole;
  betriebe: string[];
  avatar?: string;
  telefon?: string;
  aktiv: boolean;
  auth_user_id?: string;
  permissions: UserPermissions;
  modulePermissions?: Record<string, boolean> | null;
  last_sign_in?: string;
  email_signatur?: string | null;
}

export interface TeamMember {
  id: string;
  vorname: string;
  nachname: string;
  email: string;
  rolle: string;
  business_units: string[];
  ist_aktiv: boolean;
  avatar_url: string | null;
  telefon: string | null;
  is_online: boolean;
  last_seen: string | null;
  created_at?: string;
  module_permissions?: Record<string, boolean> | null;
  email_signatur?: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  isAdmin: boolean;
  hasAccess: (betrieb: string) => boolean;
  hasModuleAccess: (module: string, minLevel?: PermissionLevel) => boolean;
  hasModuleVisible: (moduleKey: string) => boolean;
  teamMembers: TeamMember[];
  onlineUserIds: Set<string>;
  refreshTeam: () => Promise<void>;
  // Admin-Ansichtswechsel (Impersonate)
  impersonateUserId: string | null;
  setImpersonateUserId: (id: string | null) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Avatar-Farben basierend auf Initialen
const AVATAR_COLORS: Record<string, string> = {
  'AK': '#B91C1C', 'ID': '#2563EB', 'VM': '#059669',
  'TR': '#7C3AED', 'IH': '#D97706', 'AM': '#0D9488',
};

function getAvatarColor(initialen: string): string {
  return AVATAR_COLORS[initialen] || '#6B7280';
}

function buildPermissions(rolle: string, businessUnits: string[]): UserPermissions {
  const vorlage = ROLLEN_VORLAGEN[rolle] || ROLLEN_VORLAGEN.mitarbeiter;
  const perms = { ...vorlage };
  
  // Betriebe basierend auf business_units setzen
  if (rolle !== 'admin') {
    perms.betrieb_gutachten = businessUnits.includes('gutachten');
    perms.betrieb_autoglas = businessUnits.includes('autoglas');
    perms.betrieb_mietwagen = businessUnits.includes('mietwagen');
    perms.betrieb_werkstatt = businessUnits.includes('werkstatt');
    perms.betrieb_protex = businessUnits.includes('protex');
    perms.betrieb_unfallgiganten = businessUnits.includes('unfallgiganten');
    perms.betrieb_airbnb = businessUnits.includes('airbnb');
    perms.betrieb_recht = businessUnits.includes('recht');
  }
  
  return perms;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(new Set());
  const presenceChannelRef = useRef<RealtimeChannel | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [impersonateUserId, setImpersonateUserId] = useState<string | null>(null);

  // Profil aus mitarbeiter-Tabelle laden
  const loadProfile = useCallback(async (authUser: User | null) => {
    if (!authUser?.email) {
      setProfile(null);
      clearSentryUser();
      return;
    }

    try {
      // Mitarbeiter aus DB laden
      const { data: maData, error: maError } = await supabase
        .from('mitarbeiter')
        .select('*')
        .ilike('email', authUser.email)
        .single();

      if (maData && !maError) {
        const initialen = `${maData.vorname?.[0] || ''}${maData.nachname?.[0] || ''}`.toUpperCase();
        const rolle = (maData.rolle || 'mitarbeiter') as UserRole;
        const businessUnits = maData.business_units || [];

        setProfile({
          id: maData.id,
          email: authUser.email,
          name: `${maData.vorname} ${maData.nachname}`,
          vorname: maData.vorname,
          nachname: maData.nachname,
          initialen,
          rolle,
          betriebe: businessUnits,
          avatar: maData.avatar_url || undefined,
          telefon: maData.telefon || undefined,
          aktiv: maData.ist_aktiv,
          auth_user_id: authUser.id,
          permissions: buildPermissions(rolle, businessUnits),
          modulePermissions: maData.module_permissions || null,
          last_sign_in: authUser.last_sign_in_at || undefined,
          email_signatur: maData.email_signatur || null,
        });
        setSentryUser({ id: maData.id, email: authUser.email, rolle });
      } else {
        // E-Mail nicht in mitarbeiter-Tabelle → Zugang verweigert
        await supabase.auth.signOut();
        setProfile(null);
        setUser(null);
        setSession(null);
        clearSentryUser();
        setError('Kein Zugang: Ihr Konto ist nicht im System registriert. Bitte Administrator kontaktieren.');
      }
    } catch {
      // Bei Fehler ebenfalls ausloggen — kein Fallback-Zugang
      await supabase.auth.signOut();
      setProfile(null);
      setUser(null);
      setSession(null);
      clearSentryUser();
      setError('Anmeldung fehlgeschlagen. Bitte erneut versuchen.');
    }
  }, []);

  // Team-Mitglieder laden
  const refreshTeam = useCallback(async () => {
    try {
      const { data, error: teamError } = await supabase
        .from('mitarbeiter')
        .select('*')
        .order('nachname');

      if (data && !teamError) {
        setTeamMembers(data.map(m => {
          // last_seen aus last_sign_in_at berechnen
          let lastSeen: string | null = null;
          if (m.last_sign_in_at) {
            const d = new Date(m.last_sign_in_at);
            const now = new Date();
            const diffMs = now.getTime() - d.getTime();
            const diffMin = Math.floor(diffMs / 60000);
            const diffH = Math.floor(diffMs / 3600000);
            const diffD = Math.floor(diffMs / 86400000);
            if (diffMin < 1) lastSeen = 'Gerade eben';
            else if (diffMin < 60) lastSeen = `Vor ${diffMin} Min.`;
            else if (diffH < 24) lastSeen = `Vor ${diffH} Std.`;
            else if (diffD < 7) lastSeen = `Vor ${diffD} Tag${diffD > 1 ? 'en' : ''}`;
            else lastSeen = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
          }
          return {
            id: m.id,
            vorname: m.vorname,
            nachname: m.nachname,
            email: m.email,
            rolle: m.rolle,
            business_units: m.business_units || [],
            ist_aktiv: m.ist_aktiv,
            avatar_url: m.avatar_url,
            telefon: m.telefon,
            is_online: onlineUserIds.has(m.email),
            last_seen: lastSeen,
            created_at: m.created_at,
            module_permissions: m.module_permissions || null,
            email_signatur: m.email_signatur || null,
          };
        }));
      }
    } catch {
      // Fehler still ignorieren
    }
  }, [onlineUserIds]);

  // Presence-Channel für Online-Status
  const setupPresence = useCallback((authUser: User) => {
    // Alten Channel aufräumen
    if (presenceChannelRef.current) {
      supabase.removeChannel(presenceChannelRef.current);
    }

    const channel = supabase.channel('team-presence', {
      config: { presence: { key: authUser.email || authUser.id } },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const onlineEmails = new Set<string>();
        Object.keys(state).forEach(key => {
          onlineEmails.add(key);
        });
        setOnlineUserIds(onlineEmails);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            email: authUser.email,
            name: authUser.user_metadata?.vorname || authUser.email,
            online_at: new Date().toISOString(),
          });
        }
      });

    presenceChannelRef.current = channel;

    // Heartbeat alle 30 Sekunden
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(() => {
      if (presenceChannelRef.current) {
        presenceChannelRef.current.track({
          email: authUser.email,
          name: authUser.user_metadata?.vorname || authUser.email,
          online_at: new Date().toISOString(),
        });
      }
    }, 30000);
  }, []);

  // Session beim Start laden.
  // Verwendet ausschließlich onAuthStateChange (inkl. INITIAL_SESSION-Event) statt
  // des separaten getSession()-Aufrufs, um Lock-Konflikte im Supabase GoTrue-Client
  // zu vermeiden. In React 19 / Strict Mode würden zwei parallele getSession()-Locks
  // zu "Lock was not released within 5000ms" und CORS-Fehlern führen.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      // INITIAL_SESSION: Initialzustand beim ersten Subscribe — ersetzt getSession()
      if (event === 'INITIAL_SESSION') {
        setSession(s);
        setUser(s?.user ?? null);
        loadProfile(s?.user ?? null);
        if (s?.user) setupPresence(s.user);
        setLoading(false);
        return;
      }

      // Bei PASSWORD_RECOVERY: Zur Reset-Seite weiterleiten, NICHT auto-login
      if (event === 'PASSWORD_RECOVERY') {
        // Session setzen damit updateUser funktioniert, aber NICHT als normalen Login behandeln
        setSession(s);
        setUser(null); // User bleibt null → kein Zugang zur App
        if (window.location.pathname !== '/reset-password') {
          window.location.href = '/reset-password';
        }
        return;
      }

      setSession(s);
      setUser(s?.user ?? null);
      loadProfile(s?.user ?? null);
      if (s?.user) {
        setupPresence(s.user);
      } else {
        // Cleanup bei Logout
        if (presenceChannelRef.current) {
          supabase.removeChannel(presenceChannelRef.current);
          presenceChannelRef.current = null;
        }
        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current);
          heartbeatRef.current = null;
        }
        setOnlineUserIds(new Set());
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      if (presenceChannelRef.current) {
        supabase.removeChannel(presenceChannelRef.current);
      }
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
    };
  }, [loadProfile, setupPresence]);

  // Team laden wenn Profil geladen ist
  useEffect(() => {
    if (profile) {
      refreshTeam();
    }
  }, [profile, refreshTeam]);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) {
      const msg = authError.message === 'Invalid login credentials'
        ? 'E-Mail oder Passwort ist falsch.'
        : authError.message;
      setError(msg);
      return { error: msg };
    }
    return { error: null };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setTeamMembers([]);
    clearSentryUser();
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (resetError) {
      return { error: resetError.message };
    }
    return { error: null };
  }, []);

  const isAdmin = profile?.rolle === 'admin';
  
  const hasAccess = useCallback((betrieb: string) => {
    if (!profile) return false;
    if (profile.rolle === 'admin') return true;
    return profile.betriebe.includes(betrieb);
  }, [profile]);

  const hasModuleAccess = useCallback((module: string, minLevel: PermissionLevel = 1) => {
    if (!profile) return false;
    if (profile.rolle === 'admin') return true;
    const key = `${module}_level` as keyof UserPermissions;
    const level = profile.permissions[key];
    if (typeof level === 'number') return level >= minLevel;
    return false;
  }, [profile]);

  // Prüft ob ein Modul in der Sidebar sichtbar ist (basierend auf module_permissions aus DB)
  const hasModuleVisible = useCallback((moduleKey: string) => {
    if (!profile) return false;
    // Admin sieht immer alles
    if (profile.rolle === 'admin') return true;
    // Wenn Impersonate aktiv, nutze die Permissions des impersonierten Users
    if (impersonateUserId) {
      const target = teamMembers.find(m => m.id === impersonateUserId);
      if (target) {
        if (target.rolle === 'admin') return true;
        if (target.module_permissions) return target.module_permissions[moduleKey] ?? false;
        // Default: nur aufgaben und kommunikation
        return moduleKey === 'aufgaben' || moduleKey === 'kommunikation';
      }
    }
    // Individuelle Permissions aus DB
    if (profile.modulePermissions) {
      return profile.modulePermissions[moduleKey] ?? false;
    }
    // Fallback: Standard-Mitarbeiter sehen nur Aufgaben + Kommunikation
    if (profile.rolle === 'mitarbeiter') {
      return moduleKey === 'aufgaben' || moduleKey === 'kommunikation';
    }
    // Sachbearbeiter: Rollen-basiert
    return hasModuleAccess(moduleKey, 1);
  }, [profile, impersonateUserId, teamMembers, hasModuleAccess]);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        error,
        signIn,
        signOut,
        resetPassword,
        isAdmin,
        hasAccess,
        hasModuleAccess,
        hasModuleVisible,
        teamMembers,
        onlineUserIds,
        refreshTeam,
        impersonateUserId,
        setImpersonateUserId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
